import type { AxiosInstance } from 'axios';
import type { IntlShape } from 'react-intl';
import type { WithPlatformDefaults } from '@lifeomic/service-clients/fhir-client';
import { formatCodingToken } from '@lifeomic/fhir-constants';
import { unwrapBundle } from '@lifeomic/phc-web-toolkit/src/util/fhir';

import { client, fhirClient } from '../../../util/api';
import { fhirSystems } from '../OnboardingSnapshot/fhir-systems';
import {
  DataSourceConfig,
  DataSourceParams,
  DataSourceReturnTypes,
  fetchResourcesForPatients,
  mapResourcesToPatient,
  patientServicePageLimit,
} from '../../workflows/datasources';
import { formatMember, isReferenceMatch, Member } from '../../workflows/utils';
import {
  ELATION_FILE_SOURCE_TYPE_CODE,
  getDocumentReferencePatientId,
  getInvalidFileReason,
} from './fhir';
import type {
  FileValidationTarget,
  FileValidationTargetLookupResult,
  InvalidFileDocumentReference,
} from './types';

export const buildInvalidFileQuery = () => ({
  bool: {
    must: [
      {
        term: {
          'meta.tag.code.keyword':
            fhirSystems.documentReference.fileOwnershipValidationStatus.values
              .invalid,
        },
      },
      {
        term: {
          'meta.tag.code.keyword': ELATION_FILE_SOURCE_TYPE_CODE,
        },
      },
    ],
  },
});

export const queryInvalidFileDocumentReferences = async (
  axiosClient: AxiosInstance,
  {
    dataset,
    cohort,
    scroll = true,
    scrollId,
  }: {
    dataset: string;
    cohort?: string;
    scroll?: string | boolean;
    scrollId?: string;
  },
) => {
  const response = await axiosClient.post<{
    took: number;
    _scroll_id: string;
    hits: {
      hits: Array<{
        _source: InvalidFileDocumentReference;
      }>;
    };
  }>(
    `/v1/fhir-search/projects/${encodeURIComponent(dataset)}${
      cohort ? `/cohorts/${encodeURIComponent(cohort)}` : ''
    }?scroll=${scrollId || scroll}`,
    {
      type: 'select',
      orderby: [
        {
          type: 'ASC',
          expr: {
            type: 'column_ref',
            column: 'indexed',
          },
        },
      ],
      from: [
        {
          table: 'document_reference',
        },
      ],
      columns: '*',
      where: {
        type: 'elasticsearch',
        query: buildInvalidFileQuery(),
      },
      limit: [
        {
          type: 'number',
          value: 0,
        },
        {
          type: 'number',
          value: 10000,
        },
      ],
    },
  );

  const documentReferences = response.data.hits.hits.map(hit => hit._source);

  return {
    documentReferences,
    scrollId: response.data._scroll_id,
    hasMore: documentReferences.length > 0,
  };
};

export const getPatientIdsFromDocumentReferences = (
  documentReferences: InvalidFileDocumentReference[],
) =>
  new Set(
    documentReferences
      .map(getDocumentReferencePatientId)
      .filter((patientId): patientId is string => !!patientId),
  );

export const fetchAllInvalidFileDocumentReferences = async ({
  dataset,
  cohort,
  axiosClient = client,
}: {
  dataset: string;
  cohort?: string;
  axiosClient?: AxiosInstance;
}) => {
  let allDocumentReferences: InvalidFileDocumentReference[] = [];
  let scrollId: string | undefined;
  let hasMore = true;

  while (hasMore) {
    const {
      documentReferences,
      scrollId: nextScrollId,
      hasMore: nextHasMore,
    } = await queryInvalidFileDocumentReferences(axiosClient, {
      dataset,
      cohort,
      scroll: 'true',
      scrollId,
    });

    allDocumentReferences = [...allDocumentReferences, ...documentReferences];
    scrollId = nextScrollId;
    hasMore = nextHasMore;
  }

  return {
    documentReferences: allDocumentReferences,
    patientIds: getPatientIdsFromDocumentReferences(allDocumentReferences),
  };
};

const invalidDocumentReferenceDataSource: DataSourceConfig<InvalidFileDocumentReference> =
  {
    key: 'invalidDocumentReferences',
    fetchData: async ({ currentPatientIds, dataset }) => {
      const response = await fhirClient.request(
        'GET /v1/fhir/dstu3/DocumentReference',
        {
          _tag: [
            dataset.codingToken,
            formatCodingToken({
              system:
                fhirSystems.documentReference.fileOwnershipValidationStatus
                  .system,
              code: fhirSystems.documentReference.fileOwnershipValidationStatus
                .values.invalid,
            }),
            formatCodingToken({
              system: fhirSystems.source.system,
              code: ELATION_FILE_SOURCE_TYPE_CODE,
            }),
          ],
          patient: currentPatientIds,
          pageSize: patientServicePageLimit,
        },
      );

      return unwrapBundle(response.data);
    },
    mapToPatient: ({ resources, patientId }) =>
      resources.filter(documentReference =>
        isReferenceMatch({ reference: documentReference?.subject, patientId }),
      ),
  };

const dataSources = {
  invalidDocumentReferences: invalidDocumentReferenceDataSource,
} as const;

export const fetchInvalidFileValidationDataSourcesForPatients = (
  params: DataSourceParams,
) => fetchResourcesForPatients(dataSources, params);

export const mapInvalidFileValidationDataToPatient = (
  resources: DataSourceReturnTypes<typeof dataSources>,
  patientId: string,
) => mapResourcesToPatient(dataSources, resources, patientId);

export const buildFileValidationTarget = ({
  patient,
  member,
  documentReference,
}: {
  patient: WithPlatformDefaults<fhir3.Patient>;
  member: Member;
  documentReference: InvalidFileDocumentReference;
}): FileValidationTarget | undefined => {
  const patientId = getDocumentReferencePatientId(documentReference);
  const documentReferenceId = documentReference.id;

  if (!patientId || !documentReferenceId) {
    return undefined;
  }

  return {
    patient,
    member,
    documentReference,
    patientId,
    documentReferenceId,
    invalidReason: getInvalidFileReason(documentReference),
  };
};

const isNotFoundError = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status === 404;

export const fetchFileValidationTargetByDocumentReferenceId = async ({
  documentReferenceId,
  expectedPatientId,
  patient,
  providers,
  formatMessage,
}: {
  documentReferenceId: string;
  expectedPatientId?: string;
  patient?: WithPlatformDefaults<fhir3.Patient>;
  providers: Array<WithPlatformDefaults<fhir3.Practitioner>>;
  formatMessage: IntlShape['formatMessage'];
}): Promise<FileValidationTargetLookupResult> => {
  try {
    const documentReferenceResponse = await fhirClient.request(
      'GET /v1/fhir/dstu3/DocumentReference/:id' as never,
      { id: documentReferenceId } as never,
    );
    const documentReference =
      documentReferenceResponse.data as InvalidFileDocumentReference;
    const patientId = getDocumentReferencePatientId(documentReference);

    if (expectedPatientId && patientId !== expectedPatientId) {
      return {
        status: 'subject-mismatch',
        expectedPatientId,
        actualPatientId: patientId,
      };
    }

    if (!patientId) {
      return { status: 'not-found' };
    }

    const hydratedPatient =
      patient && patient.id === patientId
        ? patient
        : (
            await fhirClient.request(
              'GET /v1/fhir/dstu3/Patient/:id' as never,
              { id: patientId } as never,
            )
          ).data;

    const target = buildFileValidationTarget({
      patient: hydratedPatient as WithPlatformDefaults<fhir3.Patient>,
      documentReference,
      member: formatMember(
        hydratedPatient as WithPlatformDefaults<fhir3.Patient>,
        {
          formatMessage,
          providers,
        },
      ),
    });

    return target ? { status: 'success', target } : { status: 'not-found' };
  } catch (error) {
    return isNotFoundError(error)
      ? { status: 'not-found' }
      : { status: 'error', error };
  }
};
